import BumbleTabs from '@bumble/app/components/BumbleTabs';
import CreateModal from '@bumble/app/components/CreateModal';
import DataTableGrid from '@bumble/app/components/DataTableGrid';
import DialogConfirm from '@bumble/app/components/DialogConfirm';
import DialogStandard from '@bumble/app/components/DialogStandard';
import { Indicator } from '@bumble/app/components/Indicator';
import { Loading } from '@bumble/app/components/Loading';
import MultipleSelections from '@bumble/app/components/MultipleSelections';
import NodeMetrics from '@bumble/app/components/NodeMetrics';
import PageContent from '@bumble/app/components/PageContent';
import PageHeader from '@bumble/app/components/PageHeader';
import { PageLoading } from '@bumble/app/components/PageLoading';
import {
  RouterAssignEntity,
  RouterCpeInfoEdit,
  RouterCreate,
  RouterDownloadResult,
  RouterHandleService,
  RouterNetworkInfoEdit,
  RouterIperfServerEdit,
  RouterPeerTest,
  RouterSiteEdit,
  RouterTabDetails,
  RouterTabLan,
  RouterGetDiagnostic,
  RouterResetLANPort,
  RouterPingAndTraceroute,
  RouterGeoVisibilityEdit,
  RouterDNIEdit,
  RouterIPMonitorTargetEdit,
  RouterDDosMitigationEdit,
  RouterBlockMaliciousEdit,
  RouterRunSpeedTest,
  RouterAnnouncement,
  RouterLinkdownRelayEdit,
  RouterSecondaryWanEdit,
  RouterPacketCapture,
  RouterExternalApiIntegrationEdit,
  RouterFeatureEdit,
  RouterFeatureMatrix,
} from '@bumble/app/components/router';
import SplitPanes from '@bumble/app/components/SplitPanes';
import { useApi } from '@bumble/app/hooks/use-api';
import { CpeInterfaces } from '@bumble/app/types/cpeTypes';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import EditIcon from '@mui/icons-material/Edit';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SpeedIcon from '@mui/icons-material/Speed';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import TroubleshootIcon from '@mui/icons-material/Troubleshoot';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import AnnouncementIcon from '@mui/icons-material/Announcement';
import { Box, Card, CardContent } from '@mui/material';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useAccount } from '../hooks/use-account';
import { apiError } from '../utils/api-error';
import Select from 'react-select';
import { RouterGenerateToken } from '@bumble/app/components/router/RouterGenerateToken';
import { RouterTrackShipping } from '../components/router/RouterTrackShipping';
import { RouterAppVisibilityEdit } from '../components/router/RouterAppVisibilityEdit';
import { CPE_MODE_PRIVATE_LINE, CPE_MODE_ROUTER } from '@bumble/app/constants';

// Features a Private Line CPE does not have (the controller rejects them): no
// secondary WAN, no wired address to test or monitor from, security features off.
const PRIVATE_LINE_HIDDEN_MENU_ITEMS = [
  'Edit Secondary WAN',
  'Start Activation Test',
  'Retrieve Activation Test Report',
  'Edit IP Monitor Target',
  'Edit iPerf Server',
  'Edit Geo Visibility',
  'Edit Block Malicious Sources',
  'Edit DDoS Mitigation',
];

export const Routers = () => {
  const api = useApi();
  const { account } = useAccount();

  const [entities, setEntities] = useState<any[]>([{label: 'None', value: 'none'}]);
  const [entity, setEntity] = useState<any>(entities[0]);

  const { isLoading: isLoading2, data: entityNames } = useQuery(
    ['prepareEntityNames'],
    api.getEntityNamesApiV1GetEntityNamesGet.bind(null, {}),
    { enabled: true, cacheTime: 0, initialData: undefined },
  );

  useEffect(() => {
    const entity_data = entityNames?.data;
    let actualEntities = [{label: 'None', value: 'none'}];
    if (entity_data && entity_data.length) {
      actualEntities = actualEntities.concat(entity_data.map((e: string) => ({
        label: e,
        value: e,
      })))
    }
    setEntities(actualEntities);
    setEntity(actualEntities[0]);
  }, [entityNames]);

  const changeEntity = (val: any) => {
    setEntity(val);
  };

  const { isLoading, data, refetch, isRefetching } = useQuery(
    ['routers', entity],
    api.getCpeApiV1GetCpeGet.bind(
      null,
      entity?.value && entity.value !== 'none' ? { entity_name: entity.value } : {}
    ),
    { enabled: true },
  );

  const columns: GridColDef[] = [
    {
      field: 'cpe_name',
      headerName: 'Name',
      //flex: 1,
      width: 200,
      disableColumnMenu: true,
    },
    {
      field: 'site_address',
      headerName: 'Site Address',
      flex: 1,
      minWidth: 300,
      disableColumnMenu: true,
    },
    {
      field: 'admin_state',
      headerName: 'Admin State',
      width: 150,
      disableColumnMenu: true,
    },
    {
      field: 'op_state',
      headerName: 'Op State',
      width: 150,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%',
          }}
        >
          {params.value !== undefined ? <Indicator active={params.value === 'up'} /> : 'N/A'}
        </Box>
      ),
    },
    {
      field: 'active_uplink',
      headerName: 'Active WAN',
      width: 150,
      disableColumnMenu: true,
    },
    {
      field: 'entity_name',
      headerName: 'Entity Name',
      width: 200,
      disableColumnMenu: true,
    },
    {
      field: 'speed_test_ip',
      headerName: 'Speed Tester IP',
      width: 150,
      disableColumnMenu: true,
      renderCell: (params) => {
        return (params.row.prod_code === 'BN-CPE-IP') ? '' : params.value;
      }
    },
    {
      field: 'provider_service_id',
      headerName: 'Provider Service ID',
      width: 150,
      disableColumnMenu: true,
    },
    {
      field: 'client_circuit_id',
      headerName: 'Client Circuit ID',
      width: 150,
      disableColumnMenu: true,
    },
    {
      field: 'account_name',
      headerName: 'Account Name',
      width: 200,
      disableColumnMenu: true,
    },
    {
      field: 'cpe_id',
      headerName: 'Device ID',
      //flex: 1,
      width: 200,
      disableColumnMenu: true,
    },
    {
      field: 'prod_code',
      headerName: 'Product Code',
      width: 150,
      disableColumnMenu: true,
    },
    //{ field: 'site_address' },
    { field: 'shipping_address' },
    { field: 'state' },
    { field: 'test_status' },
    { field: 'description' },
    {
      field: 'source_ip',
      headerName: 'Source IP',
      width: 150,
      disableColumnMenu: true,
      renderCell: (params) => {
        return (params.row.prod_code === 'BN-CPE-IP') ? params.value : '';
      }
    },
    {
      field: 'site_street_address',
      headerName: 'Site Street Address',
      width: 200,
      disableColumnMenu: true,
      // Use valueGetter (not renderCell) so the search box's quick filter can
      // match this column. The structured address lives under
      // site_address_structured, so without a valueGetter the grid reads
      // row.site_street_address (undefined) and the column is unsearchable.
      valueGetter: (_value, row) => row.site_address_structured?.street_address ?? '',
    },
    {
      field: 'site_city',
      headerName: 'Site City',
      width: 150,
      disableColumnMenu: true,
      valueGetter: (_value, row) => row.site_address_structured?.city ?? '',
    },
    {
      field: 'site_state_province',
      headerName: 'Site State/Province',
      width: 150,
      disableColumnMenu: true,
      valueGetter: (_value, row) => row.site_address_structured?.state_province ?? '',
    },
    {
      field: 'site_postal_code',
      headerName: 'Site Postal Code',
      width: 130,
      disableColumnMenu: true,
      valueGetter: (_value, row) => row.site_address_structured?.postal_code ?? '',
    },
    {
      field: 'site_country',
      headerName: 'Site Country',
      width: 130,
      disableColumnMenu: true,
      valueGetter: (_value, row) => row.site_address_structured?.country ?? '',
    },
  ];

  const columnVisible = {
    cpe_name: true,
    cpe_id: true,
    prod_code: true,
    admin_state: true,
    op_state: true,
    speed_test_ip: true,
    provider_service_id: true,
    client_circuit_id: true,
    entity_id: true,
    account_name: account?.master_account,
    //entity_name: account?.master_account,
    entity_name: true,
    site_address: true,
    shipping_address: false,
    state: false,
    test_status: false,
    description: false,
    site_street_address: true,
    site_city: true,
    site_state_province: true,
    site_postal_code: true,
    site_country: true,
  };

  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const [selectedItemId, setSelectedItemId] = useState<string>('');
  const [showConfirmDelete, setShowConfirmDelete] = useState<boolean>(false);
  const [loadingModalOpen, setLoadingModalOpen] = useState<boolean>(false);
  const [openCreateModal, setOpenCreateModal] = useState<boolean>(false);
  const [showEditCPEInfoDialog, setShowEditCPEInfoDialog] = useState<boolean>(false);
  const [showEditNetworkInfoDialog, setShowEditNetworkInfoDialog] = useState<boolean>(false);
  const [showEditIPMonitorDialog, setShowEditIPMonitorDialog] = useState<boolean>(false);
  const [showEditDNIDialog, setShowEditDNIDialog] = useState<boolean>(false);
  const [showEditIperfDialog, setShowEditIperfDialog] = useState<boolean>(false);
  const [showEditAppVisibility, setShowEditAppVisibility] = useState<boolean>(false);
  const [showEditLinkDownDelay, setShowEditLinkDownDelay] = useState<boolean>(false);
  const [showEditExternalApiIntegration, setShowEditExternalApiIntegration] = useState<boolean>(false);
  const [showEditFeatureDialog, setShowEditFeatureDialog] = useState<boolean>(false);
  const [showEditGeoDialog, setShowEditGeoDialog] = useState<boolean>(false);
  const [showEditBlockMalicious, setShowEditBlockMalicious] = useState<boolean>(false);
  const [showEditDDosMitigation, setShowEditDDosMitigation] = useState<boolean>(false);
  const [showPeerTestDialog, setShowPeerTestDialog] = useState<boolean>(false);
  const [showRunSpeedTest, setShowRunSpeedTest] = useState<boolean>(false);
  const [showRetrieveResultDialog, setShowRetrieveResultDialog] = useState<boolean>(false);
  const [showAssignEntity, setShowAssignEntity] = useState<boolean>(false);
  const [showEditSecondaryWan, setShowEditSecondaryWan] = useState<boolean>(false);
  const [showEditSiteDialog, setShowEditSiteDialog] = useState<boolean>(false);
  const [showGetDiagnosticDialog, setShowGetDiagnosticDialog] = useState<boolean>(false);
  const [showResetLANPortsDialog, setShowResetLANPortsDialog] = useState<boolean>(false);
  const [showResetDeviceDialog, setShowResetDeviceDialog] = useState<boolean>(false);
  const [showPingDialog, setShowPingDialog] = useState<boolean>(false);
  const [showPacketCaptureDialog, setShowPacketCaptureDialog] = useState<boolean>(false);
  const [showGenerateTokenDialog, setShowGenerateTokenDialog] = useState<boolean>(false);
  const [showTrackShippingDialog, setShowTrackShippingDialog] = useState<boolean>(false);
  const [showAnnouncement, setShowAnnouncement] = useState<boolean>(false);
  const [generatedToken, setGeneratedToken] = useState<string>('');
  const [mode, setMode] = useState<{ value: number, flag?: boolean }>({ value: 0 });
  const [showHanldeServiceDialog, setShowHandleServiceDialog] = useState<{
    status: boolean, value: boolean
  }>({ status: true, value: false });
  const [menuItems, setMenuItems] = useState<{
    label: string;
    icon: React.ReactNode;
    onClick: () => void;
    hasDivider?: boolean;
    disabled?: boolean;
    // Its dialog reads the CPE detail, so it waits until that is loaded and current.
    needsDetail?: boolean;
  }[]>([]);
  const [speedtestIntervalId, setSpeedtestIntervalId] = useState<number | null>(null);

  const selectedRowItem = useMemo(() => {
    return rowSelectionModel.length === 1 ? rowSelectionModel[0].toString() : '';
  }, [rowSelectionModel]);

  const {
    isLoading: detailLoading,
    isFetching: detailFetching,
    isSuccess: detailSuccess,
    data: detailData,
    refetch: detailRefetch,
  } = useQuery(
    ['routerDetail', selectedRowItem],
    api.getCpeDetailApiV1GetCpeDetailGet.bind(null, {
      cpe_id: selectedRowItem,
    }),
    {
      enabled: !!selectedRowItem,
      refetchOnWindowFocus: false,
      // The global handler only reports a failure with no data; with cached data the detail
      // actions stay disabled, so say why.
      onError: () => {
        if (detailData) toast.error('Could not refresh the CPE details. Use Refresh to try again.');
      },
    },
  );

  // The list row is used until the detail loads, so Private Line items never flash in the menu.
  const selectedRow = useMemo(
    () => data?.data.find((item) => item.cpe_id === selectedRowItem),
    [data, selectedRowItem],
  );
  const cpeMode = detailData?.data.mode || selectedRow?.mode;
  const isPrivateLine = cpeMode === CPE_MODE_PRIVATE_LINE;
  // Not while refetching, or after a failed refetch: the cached detail may be out of date, and
  // edit dialogs save what they open with.
  const detailReady = detailSuccess && !detailFetching;

  const removeRouter = useMutation(api.removeCpeApiV1RemoveCpePost, {
    onSuccess() {
      toast.success('CPE router removed.');
      setLoadingModalOpen(false);
      handleDataRefetch();
    },
    onError(error: any) {
      apiError(error);
      setLoadingModalOpen(false);
    },
  });

  const collectDiagBundle = useMutation(api.collectDiagBundleApiV1CollectDiagBundlePost, {
    onSuccess(_data, variables) {
      toast.success(`Diagnostics upload initiated on CPE ${variables.node_id}`);
      setLoadingModalOpen(false);
    },
    onError(error: any) {
      apiError(error);
      setLoadingModalOpen(false);
    },
  });

  const resetNode = useMutation(api.resetNodeApiV1ResetNodePost, {
    onSuccess() {
      toast.success('CPE router reseted successully.');
      setLoadingModalOpen(false);
      handleDataRefetch();
    },
    onError(error: any) {
      apiError(error);
      setLoadingModalOpen(false);
    },
  })

  useEffect(() => {
    let items: typeof menuItems = [];
    switch (mode.value) {
      case 1:
        items.push(...[{
          label: 'Edit Site',
          icon: <EditIcon color="info" fontSize="small" />,
          onClick: handleEditSite,
          needsDetail: true,
        }, {
          label: 'Activate Service',
          icon: <PlayArrowIcon color="info" fontSize="small" />,
          onClick: () => handleService(true),
          needsDetail: true,
          hasDivider: true,
          disabled: !mode?.flag
        }, {
          label: 'Stop Service',
          icon: <PauseIcon color="info" fontSize="small" />,
          onClick: () => handleService(false),
          needsDetail: true,
          hasDivider: false,
          disabled: mode?.flag
        }]);
        break;
      default:
        items.push(...[{
          label: 'Edit CPE Info',
          icon: <EditIcon color="info" fontSize="small" />,
          onClick: handleEditCPEInfo,
          needsDetail: true,
        }, {
          label: 'Edit Network Info',
          icon: null,
          onClick: handleEditNetworkInfo,
          needsDetail: true,
        }, {
          label: 'Assign Entity',
          icon: null,
          onClick: () => setShowAssignEntity(true),
          needsDetail: true,
        },{
          label: 'Edit Secondary WAN',
          icon: null,
          onClick: () => setShowEditSecondaryWan(true),
          needsDetail: true,
        }, ...(account?.master_account ? [{
          label: 'Edit Feature',
          icon: null,
          onClick: handleEditFeature,
          needsDetail: true,
        }] : []), {
          label: 'Run Speed Test',
          icon: <SpeedIcon color="info" fontSize="small" />,
          onClick: handleRunSpeedTest,
          needsDetail: true,
          hasDivider: true,
        }, {
          label: 'Start Activation Test',
          icon: null,
          onClick: handlePeerTest,
          needsDetail: true,
        }, {
          label: 'Retrieve Activation Test Report',
          icon: null,
          onClick: handleRetrieveResult,
        }, {
          label: 'Edit Down Notification Interval',
          icon: <ToggleOnIcon color="info" fontSize="small" />,
          onClick: handleEditDownNotificationInterval,
          needsDetail: true,
          hasDivider: true,
        }, {
          label: 'Edit IP Monitor Target',
          icon: null,
          onClick: handleEditIPMonitor,
          needsDetail: true,
        }, {
          label: 'Edit iPerf Server',
          icon: null,
          onClick: handleEditIperfServer,
          needsDetail: true,
        }, {
          label: 'Edit App Visibility',
          icon: null,
          onClick: handleEditAppVisibility,
          needsDetail: true,
        }, {
          label: 'Edit Link-Down Relay',
          icon: null,
          onClick: handleEditLinkDownDelay,
          needsDetail: true,
        }, {
          label: 'Assign External API Integration',
          icon: null,
          onClick: handleEditExternalApiIntegration,
        }, {
          label: 'Edit Geo Visibility',
          icon: <TravelExploreIcon color="info" fontSize="small" />,
          onClick: handleEditGeo,
          needsDetail: true,
          hasDivider: true,
        }, {
          label: 'Edit Block Malicious Sources',
          icon: null,
          onClick: handleBlockMalicious,
          needsDetail: true,
        }, {
          label: 'Edit DDoS Mitigation',
          icon: null,
          onClick: handleDDosMitigation,
          needsDetail: true,
        }]);
        if (isPrivateLine) {
          items = items.filter((item) => !PRIVATE_LINE_HIDDEN_MENU_ITEMS.includes(item.label));
        }
        break;
    }
    const deleteItem = {
      label: 'Delete',
      icon: <DeleteForeverOutlinedIcon color="error" fontSize="small" />,
      onClick: () => setShowConfirmDelete(true),
      hasDivider: true,
    };
    // Internet CPE (BN-CPE-IP, mode.value === 1) only supports site/service management,
    // so the diagnostics/device-maintenance block does not apply -- keep just Delete.
    if (mode.value === 1) {
      items.push(deleteItem);
    } else {
      items.push(...[{
        label: 'Get Diagnostics',
        icon: <TroubleshootIcon color="info" fontSize="small" />,
        onClick: handleGetDiagnostic,
        hasDivider: true,
      }, {
        label: 'Ping and Traceroute',
        icon: null,
        onClick: handlePing,
      }, {
        label: 'Packet Capture',
        icon: null,
        onClick: handlePacketCapture,
      }, {
        label: 'Reset LAN Ports',
        icon: null,
        onClick: handleResetLAN,
      }, {
        label: 'Reset Device',
        icon: null,
        onClick: handleResetDevice,
      }, {
        label: 'Generate Registration Token',
        icon: null,
        onClick: handleGenerateNodeToken,
      }, {
        label: 'Upload Diagnostics',
        icon: null,
        onClick: handleCollectDiagBundle,
      }, {
        label: 'Track Shipping',
        icon: <LocalShippingOutlinedIcon color="info" fontSize="small" />,
        onClick: handleTrackShipping,
        needsDetail: true,
        hasDivider: true,
      }, {
        label: 'Announcement',
        icon: <AnnouncementIcon color="info" fontSize="small" />,
        onClick: handleAnnouncement,
        needsDetail: true,
        hasDivider: true,
      }, deleteItem]);
    }
    setMenuItems(items.map((item) => (
      item.needsDetail ? { ...item, disabled: item.disabled || !detailReady } : item
    )));
  }, [mode, isPrivateLine, detailReady, account?.master_account, selectedItemId]);

  useEffect(() => {
    if (rowSelectionModel.length === 1) {
      setSelectedItemId(selectedRowItem);
      data?.data.filter(item => item.cpe_id === selectedRowItem).map((item) => {
        switch (item.prod_code) {
          case 'BN-CPE-IP':
            setMode({ value: 1, flag: item.admin_state === 'active' ? false : true });
            break;
          default:
            setMode({ value: 0 });
        }
      })
    } else setSelectedItemId('');
  }, [rowSelectionModel, selectedRowItem]);

  const handleSelectionChange = (newSelection: GridRowSelectionModel) => {
    setRowSelectionModel(newSelection.slice(-1));
  };


  const handleDataRefetch = () => {
    refetch();
    if (selectedRowItem) detailRefetch();
    toast.success('Refreshed internet CPEs.');
  };

  const handleCreateModalOpen = () => {
    setOpenCreateModal(true);
  };

  const handleCreateModalClose = () => {
    setOpenCreateModal(false);
  };

  const handleRouterDelete = () => {
    setLoadingModalOpen(true);
    removeRouter.mutate({ cpe_id: selectedItemId });
  };

  const handleRouterResetDevice = () => {
    setLoadingModalOpen(true);
    resetNode.mutate({
      node_id: selectedItemId,
      hard: true,
    })
  }

  const handleCollectDiagBundle = () => {
    setLoadingModalOpen(true);
    // Asked for by hand, so upload even over cellular.
    collectDiagBundle.mutate({ node_id: selectedItemId, allow_cellular: true });
  };

  const handleEditCPEInfo = () => {
    setShowEditCPEInfoDialog(true);
  };

  const handleEditIPMonitor = () => {
    setShowEditIPMonitorDialog(true);
  };

  const handleEditDownNotificationInterval = () => {
    setShowEditDNIDialog(true);
  };

  const handleEditNetworkInfo = () => {
    setShowEditNetworkInfoDialog(true);
  };

  const handleEditIperfServer = () => {
    setShowEditIperfDialog(true);
  }

  const handleEditAppVisibility = () => {
    setShowEditAppVisibility(true);
  };

  const handleEditLinkDownDelay = () => {
    setShowEditLinkDownDelay(true);
  };

  const handleEditExternalApiIntegration = () => {
    setShowEditExternalApiIntegration(true);
  };

  const handleEditFeature = () => {
    setShowEditFeatureDialog(true);
  };


  const handleEditGeo = () => {
    setShowEditGeoDialog(true);
  }

  const handleBlockMalicious = () => {
    setShowEditBlockMalicious(true);
  }

  const handleDDosMitigation = () => {
    setShowEditDDosMitigation(true);
  }

  const handleAnnouncement = () => {
    setShowAnnouncement(true);
  }

  const handlePeerTest = () => {
    setShowPeerTestDialog(true);
  }

  const handleRunSpeedTest = () => {
    setShowRunSpeedTest(true);
  }

  const handleRetrieveResult = () => {
    setShowRetrieveResultDialog(true);
  }

  const handleService = (status: boolean) => {
    setShowHandleServiceDialog({ status, value: true });
  }

  const handleEditSite = () => {
    setShowEditSiteDialog(true);
  }

  const handleGetDiagnostic = () => {
    setShowGetDiagnosticDialog(true);
  }

  const handleResetLAN = () => {
    setShowResetLANPortsDialog(true);
  }

  const handleResetDevice = () => {
    setShowResetDeviceDialog(true);
  }

  const handlePing = () => {
    setShowPingDialog(true);
  }

  const handlePacketCapture = () => {
    setShowPacketCaptureDialog(true);
  };

  const handleGenerateNodeToken = () => {
    setShowGenerateTokenDialog(true);
    generateRefetch();
  }

  const handleTrackShipping = () => {
    setShowTrackShippingDialog(true);
  }

  const {
    isLoading: generateLoading,
    data: generateData,
    refetch: generateRefetch,
    fetchStatus: generateStatus,
    isRefetching: generateRefetching
  } = useQuery(
    ['generateToken'],
    api.generateNodeTokenApiV1GenerateNodeTokenPost.bind(null, {node_id: selectedItemId}),
    { enabled: false, retry: false, initialData: undefined, onError: apiError },
  )

  useEffect(() => {
    if (generateData?.data) {
      const parsedData = generateData.data;
      setGeneratedToken(parsedData);
    }
  }, [generateData]);

  const handlePeerTestDialogClose = (cpeId: string, sse_id: string) => {
    setShowPeerTestDialog(false);
    if (sse_id) {
      startSSE(cpeId, sse_id);
    }
  };

  let eventSource: any;
  let retryTimeout = 1000;
  let maxTimeout = 30000;
  let reconnecting = false;

  const startSSE = (cpe_id: string, sse_id: string) => {
    if (eventSource) eventSource.close();
    eventSource = new EventSource(`${import.meta.env.VITE_API}/check_cpe_test_status?cpe_id=${cpe_id}&sse_id=${sse_id}`);
    eventSource.onmessage = (event: any) => {
      toast.success(event.data, {autoClose: false});
      retryTimeout = 1000;
      stopSSE();
    },
    eventSource.onerror = (event: any) => {
      console.error('Connection lost.');
      if (eventSource.readyState === EventSource.CLOSED) {
        console.log('Server closed connection normally. Not reconnecting.');
        return;
      }
      console.log('Retrying in', retryTimeout / 1000, 'seconds...');
      eventSource.close();
      eventSource = null;
      if (!reconnecting) {
        reconnecting = true;
        setTimeout(() => {
          reconnecting = false;
          retryTimeout = Math.min(retryTimeout * 2, maxTimeout);
          startSSE(cpe_id, sse_id);
        }, retryTimeout);
      }
    };
  }

  const stopSSE = () => {
    if (eventSource) {
      eventSource.close();
      eventSource = null;
      console.log('SSE connection closed by user.');
    }
  }

  useEffect(() => {
    if (!detailData) return;
    switch (detailData.data.prod_code) {
      case 'BN-CPE-IP':
        setMode({
          value: 1,
          flag: detailData?.data.admin_state === 'active' ? false : true
        });
        break;
      default:
        setMode({ value: 0 });
    }
  }, [detailData])

  return (
    <PageContent>
      <PageHeader pageName="Internet CPE" count={data?.data?.length || 0} />
      <Box sx={{ mb: 5, position: 'absolute', top: 140, left: 570 }}>
        <Select options={entities} defaultValue={entities[0]} value={entity} onChange={changeEntity} styles={{
          control: (baseStyles) => ({
            ...baseStyles,
            borderRadius: '8px',
            width: '300px',
          }),
          menu: (baseStyles) => ({
            ...baseStyles,
            width: '300px',
          }),
        }}/>
      </Box>
      {isLoading ? <Loading /> : (<>
        <SplitPanes
          top={
            <Card elevation={0}>
              <CardContent>
                <DataTableGrid
                  rows={
                    data?.data.map((item) => {
                      return {
                        ...item,
                        id: item.cpe_id,
                      };
                    }) || []
                  }
                  columns={columns}
                  handleSelectionChange={handleSelectionChange}
                  rowSelectionModel={rowSelectionModel}
                  itemsSelected={rowSelectionModel.length === 1}
                  newLabel="Create Device"
                  newOnClick={handleCreateModalOpen}
                  columnVisibilityModel={columnVisible}
                  refreshData={handleDataRefetch}
                  isRefetching={isRefetching}
                  menuItems={menuItems}
                  pagination={true}
                />
              </CardContent>
            </Card>
          }
          bottom={
            <Card elevation={0} sx={{ mt: 5 }}>
              <CardContent>
                {rowSelectionModel.length === 1 ? (
                  <BumbleTabs
                    tabs={mode.value == 1 ? [
                      {
                        el: <RouterTabDetails data={detailData?.data} isLoading={detailLoading} mode={mode.value} onInterfaceUpdated={() => detailRefetch()} />,
                        label: 'Details',
                        id: 0,
                      }
                    ] : [
                      {
                        el: <RouterTabDetails data={detailData?.data} isLoading={detailLoading} onInterfaceUpdated={() => detailRefetch()} />,
                        label: 'Details',
                        id: 0,
                      },
                      {
                        el: <NodeMetrics id={selectedRowItem} />,
                        label: 'Monitoring',
                        id: 1,
                      },
                      {
                        el: <RouterTabLan cpeId={selectedRowItem} />,
                        label: 'LAN',
                        id: 2,
                      },
                      ...(account?.master_account ? [{
                        el: <RouterFeatureMatrix cpeId={selectedRowItem} />,
                        label: 'Feature',
                        id: 3,
                      }] : []),
                    ]}
                  />
                ) : (
                  <MultipleSelections count={rowSelectionModel.length} />
                )}
              </CardContent>
            </Card>
          }
        />
      </>)}
      {openCreateModal && (
        <CreateModal
          open={openCreateModal}
          handleClose={handleCreateModalClose}
          title="Create Device"
        >
          <RouterCreate
            handleClose={handleCreateModalClose}
            onSubmit={(entityName) => {
              // The new CPE only shows up in the list if its entity matches
              // the currently selected entity filter — switch to it so the
              // newly created device is immediately visible rather than
              // silently filtered out.
              const matchedEntity = entities.find((e) => e.value === (entityName || 'none'));
              if (matchedEntity && matchedEntity.value !== entity?.value) {
                setEntity(matchedEntity);
              } else {
                refetch();
              }
            }}
          />
        </CreateModal>
      )}
      {showEditCPEInfoDialog && selectedItemId && detailData && (
        <DialogStandard
          open={showEditCPEInfoDialog}
          handleClose={() => setShowEditCPEInfoDialog(false)}
          title="Edit CPE info"
        >
          <RouterCpeInfoEdit
            onSubmit={() => {
              setShowEditCPEInfoDialog(false);
              refetch();
              detailRefetch();
            }}
            cpeId={selectedItemId}
            cpeName={detailData?.data?.cpe_name}
            shippingAddress={detailData?.data?.shipping_address_structured}
            siteAddress={detailData?.data?.site_address_structured}
            providerServiceID={detailData?.data?.provider_service_id || ''}
            clientCircuitID={detailData?.data?.client_circuit_id || ''}
            carrierInfo={detailData?.data?.carrier_info || ''}
            localSupportName={detailData?.data?.local_support_contact_name || ''}
            localSupportEmail={detailData?.data?.local_support_contact_email || ''}
            localSupportPhone={detailData?.data?.local_support_contact_phone || ''}
            shippingContactName={detailData?.data?.contact_name || ''}
            shippingContactEmail={detailData?.data?.contact_email || ''}
            shippingContactPhone={detailData?.data?.contact_phone || ''}
            description={detailData?.data?.description || ''}
            pingEmailNotification={detailData?.data?.ping_email_notification || ''}
            pingWebhookNotification={detailData?.data?.ping_webhook_notification || ''}
          />
        </DialogStandard>
      )}
      {showEditNetworkInfoDialog && selectedItemId && detailData && (
        <DialogStandard
          open={showEditNetworkInfoDialog}
          handleClose={() => setShowEditNetworkInfoDialog(false)}
          title="Edit Network Info"
        >
          <RouterNetworkInfoEdit
            onSubmit={() => {
              setShowEditNetworkInfoDialog(false);
              refetch();
              detailRefetch();
            }}
            cpeId={selectedItemId}
            prodCode={detailData.data.prod_code}
            mode={cpeMode || CPE_MODE_ROUTER}
            ports={detailData.data.ports as CpeInterfaces}
            additionalLanPorts={detailData.data.additional_lan_ports}
          />
        </DialogStandard>
      )}
      {showEditIPMonitorDialog && selectedItemId && detailData && (
        <DialogStandard
          open={showEditIPMonitorDialog}
          handleClose={() => setShowEditIPMonitorDialog(false)}
          title="Edit IP Monitor Target"
        >
          <RouterIPMonitorTargetEdit
            onSubmit={() => {
              setShowEditIPMonitorDialog(false);
              refetch();
              detailRefetch();
            }}
            cpeId={selectedItemId}
            ipMonitorTarget={detailData.data.ip_monitor_target || ''}
          />
        </DialogStandard>
      )}
      {showEditDNIDialog && selectedItemId && detailData && (
        <DialogStandard
          open={showEditDNIDialog}
          handleClose={() => setShowEditDNIDialog(false)}
          title="Edit Down Notification Interval"
        >
          <RouterDNIEdit
            onSubmit={() => {
              setShowEditDNIDialog(false);
              refetch();
              detailRefetch();
            }}
            cpeId={selectedItemId}
            scanGap={detailData?.data.scan_gap || 1}
            overrideEmail={detailData?.data.override_email || ""}
            notificationMutedUntil={detailData?.data.notification_muted_until || null}
            wanFlapAlertEnabled={detailData?.data.wan_flap_alert_enabled}
          />
        </DialogStandard>
      )}
      {showEditIperfDialog && selectedItemId && detailData && (
        <DialogStandard
          open={showEditIperfDialog}
          handleClose={() => setShowEditIperfDialog(false)}
          title="Edit iPerf Server"
        >
          <RouterIperfServerEdit
            onSubmit={() => {
              setShowEditIperfDialog(false);
              refetch();
              detailRefetch();
            }}
            cpeId={selectedItemId}
            iPerfServerEnabled={detailData.data.iperf_server || false}
          />
        </DialogStandard>
      )}
      {showEditAppVisibility && selectedItemId && detailData && (
        <DialogStandard
          open={showEditAppVisibility}
          handleClose={() => setShowEditAppVisibility(false)}
          title="Edit App Visibility"
        >
          <RouterAppVisibilityEdit
            onSubmit={() => {
              setShowEditAppVisibility(false);
              refetch();
              detailRefetch();
            }}
            cpeId={selectedItemId}
            appVisibilityEnabled={detailData.data.app_visibility_enabled || false}
          />
        </DialogStandard>
      )}
      {showEditLinkDownDelay && selectedItemId && detailData && (
        <DialogStandard
          open={showEditLinkDownDelay}
          handleClose={() => setShowEditLinkDownDelay(false)}
          title="Edit Link-Down Relay"
        >
          <RouterLinkdownRelayEdit
            onSubmit={() => {
              setShowEditLinkDownDelay(false);
              refetch();
              detailRefetch();
            }}
            cpeId={selectedItemId}
            linkDownRelay={detailData.data.link_relay || false}
            privateLine={isPrivateLine}
          />
        </DialogStandard>
      )}
      {showEditExternalApiIntegration && selectedItemId && (
        <DialogStandard
          open={showEditExternalApiIntegration}
          handleClose={() => setShowEditExternalApiIntegration(false)}
          title="Assign External API Integration"
        >
          <RouterExternalApiIntegrationEdit
            onSubmit={() => setShowEditExternalApiIntegration(false)}
            cpeId={selectedItemId}
          />
        </DialogStandard>
      )}
      {showEditFeatureDialog && account?.master_account && selectedItemId && detailData && (
        <DialogStandard
          open={showEditFeatureDialog}
          handleClose={() => setShowEditFeatureDialog(false)}
          title="Edit Feature"
        >
          <RouterFeatureEdit
            onSubmit={() => setShowEditFeatureDialog(false)}
            cpeId={selectedItemId}
            prodCode={detailData.data.prod_code}
          />
        </DialogStandard>
      )}
      {showEditGeoDialog && selectedItemId && detailData && (
        <DialogStandard
          open={showEditGeoDialog}
          handleClose={() => setShowEditGeoDialog(false)}
          title="Edit Geo Visibility"
        >
          <RouterGeoVisibilityEdit
            onSubmit={() => {
              setShowEditGeoDialog(false);
              refetch();
              detailRefetch();
            }}
            cpeId={selectedItemId}
            geoEnabled={(detailData.data.monitor_inbound_pub_ip_enabled && detailData.data.monitor_outbound_pub_ip_enabled) || false}
          />
        </DialogStandard>
      )}
      {showEditBlockMalicious && selectedItemId && detailData && (
        <DialogStandard
          open={showEditBlockMalicious}
          handleClose={() => setShowEditBlockMalicious(false)}
          title="Edit Block Malicious Sources"
        >
          <RouterBlockMaliciousEdit
            onSubmit={() => {
              setShowEditBlockMalicious(false);
              refetch();
              detailRefetch();
            }}
            cpeId={selectedItemId}
            isEnabled={detailData.data.malicious_ip_block_enabled || false}
          />
        </DialogStandard>
      )}
      {showEditDDosMitigation && selectedItemId && detailData && (
        <DialogStandard
          open={showEditDDosMitigation}
          handleClose={() => setShowEditDDosMitigation(false)}
          title="Edit DDoS Mitigation"
        >
          <RouterDDosMitigationEdit
            onSubmit={() => {
              setShowEditDDosMitigation(false);
              refetch();
              detailRefetch();
            }}
            cpeId={selectedItemId}
            dDosEnabled={detailData.data.denial_of_service_enabled || false}
          />
        </DialogStandard>
      )}
      {showAnnouncement && selectedItemId && detailData && (
        <DialogStandard
          open={showAnnouncement}
          handleClose={() => setShowAnnouncement(false)}
          title="Announcement"
        >
          <RouterAnnouncement
            cpeId={selectedItemId}
            onSubmit={() => {
              setShowAnnouncement(false);
              refetch();
              detailRefetch();
            }}
          />
        </DialogStandard>
      )}
      {showPeerTestDialog && selectedItemId && detailData && (
        <DialogStandard
          open={showPeerTestDialog}
          handleClose={() => setShowPeerTestDialog(false)}
          title="Start Activation Test"
        >
          <RouterPeerTest
            cpeId={selectedItemId}
            close={handlePeerTestDialogClose}
          />
        </DialogStandard>
      )}
      {showRetrieveResultDialog && selectedItemId && (
        <DialogStandard
          size="md"
          open={showRetrieveResultDialog}
          handleClose={() => setShowRetrieveResultDialog(false)}
          title="Retrieve Activation Test Report"
        >
          <RouterDownloadResult cpeId={selectedItemId}/>
        </DialogStandard>
      )}
      {showRunSpeedTest && selectedItemId && detailData && (
        <DialogStandard
          open={showRunSpeedTest}
          handleClose={() => {
            if (speedtestIntervalId !== null) {
              clearInterval(speedtestIntervalId);
              setSpeedtestIntervalId(null);
            }
            setShowRunSpeedTest(false);
          }}
          title="Run Speed Test"
        >
          <RouterRunSpeedTest
            cpeId={selectedItemId}
            setIntervalId={setSpeedtestIntervalId}
            privateLine={isPrivateLine}
          />
        </DialogStandard>
      )}
      {showGetDiagnosticDialog && selectedItemId && (
        <DialogStandard
          size="md"
          open={showGetDiagnosticDialog}
          handleClose={() => setShowGetDiagnosticDialog(false)}
          title="Diagnostics Results"
        >
          <RouterGetDiagnostic
            cpeId={selectedItemId}
            onSubmit={() => setShowGetDiagnosticDialog(false)}
          />
        </DialogStandard>
      )}
      {showResetLANPortsDialog && selectedItemId && (
        <DialogStandard
          open={showResetLANPortsDialog}
          handleClose={() => setShowResetLANPortsDialog(false)}
          title="Reset LAN Ports"
        >
          <RouterResetLANPort
            cpeId={selectedItemId}
            onSubmit={ () =>{
              setShowResetLANPortsDialog(false);
              refetch();
              detailRefetch();
            }}
          />
        </DialogStandard>
      )}
      {showResetDeviceDialog && (
        <DialogConfirm
          open={showResetDeviceDialog}
          handleClose={() => setShowResetDeviceDialog(false)}
          handleConfirm={handleRouterResetDevice}
          title="Reset CPE?"
          description={`Are you certain you wish to proceed with resetting ${selectedItemId}? Please note that this action is irreversible.`}
        />
      )}
      {showPingDialog && selectedItemId && (
        <DialogStandard
          size="md"
          open={showPingDialog}
          handleClose={() => setShowPingDialog(false)}
          title="Ping & Traceroute"
        >
          <RouterPingAndTraceroute
            cpeId={selectedItemId}
            cpeName={detailData?.data?.cpe_name ?? selectedRow?.cpe_name}
            privateLine={isPrivateLine}
          />
        </DialogStandard>
      )}
      {showPacketCaptureDialog && selectedItemId && (
        <DialogStandard
          size="xs"
          open={showPacketCaptureDialog}
          handleClose={() => setShowPacketCaptureDialog(false)}
          title="Packet Capture"
        >
          <RouterPacketCapture
            cpeId={selectedItemId}
          />
        </DialogStandard>
      )}
      {showGenerateTokenDialog && selectedItemId && (
        <DialogStandard
          size="md"
          open={showGenerateTokenDialog}
          handleClose={() => setShowGenerateTokenDialog(false)}
          title="Generate Registration Token"
        >
          <RouterGenerateToken
            generatedToken = {generatedToken}
            loading = {generateLoading || generateRefetching}
            onClose= {() => setShowGenerateTokenDialog(false)}
          />
        </DialogStandard>
      )}
      {showTrackShippingDialog && selectedItemId && detailData && (
        <DialogStandard
          open={showTrackShippingDialog}
          handleClose={() => setShowTrackShippingDialog(false)}
          title="Track Shipping"
        >
          <RouterTrackShipping
            cpeId = {selectedItemId}
            shippingTrackNumber = {detailData.data.tracking_number || ''}
            onSubmit={ () =>{
              setShowTrackShippingDialog(false);
              refetch();
              detailRefetch();
            }}
          />
        </DialogStandard>
      )}
      {showConfirmDelete && (
        <DialogConfirm
          open={showConfirmDelete}
          handleClose={() => setShowConfirmDelete(false)}
          handleConfirm={handleRouterDelete}
          title="Delete CPE router?"
          description={`Are you certain you wish to proceed with deleting ${selectedItemId}? Please note that this action is irreversible and will result in the permanent removal of CPE router.`}
          confirmationPhrase={selectedItemId}
        />
      )}
      {showHanldeServiceDialog.value && selectedItemId && detailData && (
        <DialogStandard
          open={showHanldeServiceDialog.value}
          handleClose={() => setShowHandleServiceDialog({ ...showHanldeServiceDialog, value: false })}
          title={showHanldeServiceDialog.status ? 'Activate Service' : 'Stop Service'}
        >
          <RouterHandleService
            cpeId={selectedItemId}
            adminState={detailData?.data?.admin_state}
            status={showHanldeServiceDialog.status}
            close={() => {
              setShowHandleServiceDialog({ ...showHanldeServiceDialog, value: false });
              refetch();
              detailRefetch();
            }}
          />
        </DialogStandard>
      )}
      {showAssignEntity && selectedItemId && detailData && (
        <DialogStandard
          open={showAssignEntity}
          handleClose={() => setShowAssignEntity(false)}
          title="Assign Entity"
        >
          <RouterAssignEntity
            onSubmit={() => {
              setShowAssignEntity(false);
              refetch();
              detailRefetch();
            }}
            cpeId={selectedItemId}
            entityName={detailData?.data?.entity_name}
          />
        </DialogStandard>
      )}
      {showEditSecondaryWan && selectedItemId && detailData && (
        <DialogStandard
          open={showEditSecondaryWan}
          handleClose={() => setShowEditSecondaryWan(false)}
          title="Edit Secondary WAN"
        >
          <RouterSecondaryWanEdit
            onSubmit={() => {
              setShowEditSecondaryWan(false);
              refetch();
              detailRefetch();
            }}
            cpeId={selectedItemId}
          />
        </DialogStandard>
      )}
      {showEditSiteDialog && selectedItemId && detailData && (
        <DialogStandard
          open={showEditSiteDialog}
          handleClose={() => setShowEditSiteDialog(false)}
          title="Edit Site info"
        >
          <RouterSiteEdit
            onSubmit={() => {
              setShowEditSiteDialog(false);
              refetch();
              detailRefetch();
            }}
            cpeId={selectedItemId}
            cpeName={detailData?.data?.cpe_name}
            siteAddress={detailData?.data?.site_address_structured}
            providerServiceID={detailData?.data?.provider_service_id || ''}
            clientCircuitID={detailData?.data?.client_circuit_id || ''}
            pingDnsIp={detailData?.data?.ping_dns_ip || ''}
            pingEmailNotification={detailData?.data?.ping_email_notification || ''}
            pingWebhookNotification={detailData?.data?.ping_webhook_notification || ''}
            defaultGatewayIP={detailData?.data?.ping_gw_ip || ''}
          />
        </DialogStandard>
      )}
      {loadingModalOpen && <PageLoading content="Working on it..." />}
    </PageContent>
  );
};
